import React, {useEffect, useState} from 'react';
import classnames from 'classnames';
import Markdown from 'markdown-to-jsx';
import {
    ContentTierInteractionType,
    IContentTier,
    IContentTierBenefit
} from '@dazn/lp-web/src/types/contentTiers.types';
import BenefitComponent from '@dazn/lp-web/src/components/ContentTiers/BenefitComponent';
import styles from '@dazn/lp-web/src/components/ContentTiers/content-tiers.module.css';
import CardHeader from '@dazn/lp-web/src/components/ContentTiers/CardHeader';
import {useLandingPagesContext} from '@dazn/lp-web/src/contexts/LandingPageContext';
import {
    triggerElementClick,
    triggerTierSelectEvent
} from '@dazn/lp-web/src/analytics/fe-events';
import {elementClickEventTypes} from '@dazn/lp-web/src/analytics/fe-events/elementClick';
import {getOffer} from '@dazn/lp-web/src/components/ContentTiers/contentTiersUtils';
import {useSavingPercentage} from '@dazn/lp-web/src/hooks/useSavingPercentage';
import {ButtonTypes, FREE_TRIAL_VARIABLE} from '@dazn/lp-web/src/types';
import localisePrice from '../../utils/localisePrice';
import {getCurrency} from '../../utils/dazn/Region';
import isQueryParamTrue from '../../utils/isQueryparamTrue';
import classNames from 'classnames';
import {showSeeMorePlansUi} from '../Header/Utils/features';
import {getSavePrice} from '../../hooks/getSavingPrice';
import {useMedia} from '../../hooks/useMedia';
import {Wrapper} from './TierContentWrapper';

interface IContentTierProps {
    contentTier: IContentTier;
    cardWidth: number;
    bestValueBadgeShown: boolean;
    billingPlan?: string;
    position?: number;
    planSelectorShown: boolean;
    preselectBillingPeriod: boolean;
    showPlanUI?: boolean;
    remainingContentTiers?: boolean;
    isHighlightedMiddleTier?: boolean;
    showWeeklyPlanAtBottom?: boolean;
    version?: string;
    priceToShow?: string;
    showWeeklyPlanAtTop?: boolean;
    index?: number;
    v3ClickedChevron?: number;
    onV3ChevronClick?: (newIndex: number) => void;
}

const applyBold = (text: string) => {
    if (!text.includes('**')) {
        return text; // If no '##' found, return the original text
    }

    return text.replace(
        /\*\*(.*?)\*\*/g,
        `<span style="font-weight: 800">$1</span>`
    );
};

const ContentTier: React.FC<IContentTierProps> = ({
    contentTier,
    cardWidth,
    bestValueBadgeShown,
    billingPlan,
    position,
    planSelectorShown = false,
    preselectBillingPeriod = false,
    showPlanUI = false,
    remainingContentTiers = false,
    isHighlightedMiddleTier = false,
    showWeeklyPlanAtBottom = false,
    showWeeklyPlanAtTop = false,
    version,
    priceToShow,
    index,
    v3ClickedChevron,
    onV3ChevronClick
}) => {
    const [tierLogos, setTierLogos] = useState<Array<string>>([]);
    const {
        showEyebrow,
        showDivider,
        showImage,
        showBenefits,
        eyebrowText,
        description,
        image,
        interactionType,
        showOverrideLogos,
        benefits,
        overrideLogos,
        entitlementSetId,
        compareEntitlementSetId,
        compareBillingPeriod,
        billingPeriod,
        showLogos,
        isCardHighlighted,
        annualDescription,
        monthlyDescription,
        offerAnnualDescription,
        offerMonthlyDescription,
        button: {
            buttonLabel: label,
            freeTrialLabel,
            action,
            buttonType,
            buttonImage = {}
        }
    } = contentTier;

    const {isMobile} = useMedia();
    const isEyebrowAdded = showEyebrow && eyebrowText;

    const {logos, getLogos, offers, promoOffers, multiplePromoOffers} =
        useLandingPagesContext();

    if (!planSelectorShown) {
        billingPlan = undefined;
    }

    useEffect(() => {
        if (!showOverrideLogos && showLogos) {
            getLogos();
        }
    }, [entitlementSetId, showLogos, showOverrideLogos]);

    useEffect(() => {
        if (showLogos && logos && logos[entitlementSetId]?.length > 0) {
            setTierLogos(logos[entitlementSetId].map((m) => m.url));
        } else if (
            showOverrideLogos &&
            overrideLogos &&
            overrideLogos?.length > 0
        ) {
            setTierLogos(overrideLogos);
        }
    }, [logos, overrideLogos]);

    const offerDetail = getOffer({
        offers,
        promoOffers,
        multiplePromoOffers,
        entitlementSetId,
        billingPeriod,
        billingPlan,
        planSelectorShown
    });

    const comparedOffer = offers?.Offers?.find(
        (offer) =>
            offer?.EntitlementSetId === compareEntitlementSetId &&
            offer?.BillingPeriod === compareBillingPeriod
    );

    const hasFreeTrial = freeTrialLabel?.includes(FREE_TRIAL_VARIABLE) ?? false;
    const totalFreeDays = offerDetail?.TotalFreeDays ?? 0;

    let buttonLabel =
        hasFreeTrial && totalFreeDays > 0
            ? freeTrialLabel?.replace(FREE_TRIAL_VARIABLE, totalFreeDays)
            : label;

    const {savingPercentage, savingPrice, savingDiscountedAnnualPrice} =
        useSavingPercentage(offerDetail, planSelectorShown);

    const savePriceResult = offers
        ? (getSavePrice(offers?.Offers, contentTier?.entitlementSetId) as {
              totalSavingPrice?: number;
              annualSavingsWithInstallment?: number;
              annualSavingsWithMonth?: number;
          })
        : {};

    const annualSavingsWithInstallment =
        savePriceResult?.annualSavingsWithInstallment;
    const annualSavingsWithMonthly = savePriceResult?.annualSavingsWithMonth;

    let modifiedDescription = description;
    if (
        planSelectorShown &&
        billingPeriod !== 'Week' &&
        billingPlan === 'Annual'
    ) {
        if (offerDetail) {
            modifiedDescription = annualDescription;
            offerDetail.ChargeTiers.forEach((tier) => {
                if (tier.Discount && tier.Discount.DiscountedMonths) {
                    modifiedDescription = offerAnnualDescription;
                }
            });
        }
        modifiedDescription =
            (modifiedDescription?.includes('%{savePercent}') ||
                modifiedDescription?.includes('%{savePrice}')) &&
            savingPercentage &&
            savingPrice &&
            modifiedDescription
                ? modifiedDescription
                      .replace(/%\{savePercent\}/g, `${savingPercentage}%`)
                      .replace(/%\{savePrice\}/g, `${savingPrice}`)
                      .replace(
                          /%\{savingDiscountedAnnualPrice\}/g,
                          `${savingDiscountedAnnualPrice}`
                      )
                : modifiedDescription;
    }
    if (planSelectorShown && billingPlan === 'Month') {
        modifiedDescription = monthlyDescription;
    }
    if (
        offerDetail &&
        offerDetail.Instalment &&
        offerDetail.Instalment.TermInMonths
    ) {
        let discountedPrice = 0;
        let actualPrice = 0;
        let discountedMonths = 0;
        let frequency = offerDetail.Instalment.TermInMonths;
        offerDetail.ChargeTiers.forEach((tier) => {
            if (tier.Currency?.toLowerCase() === getCurrency()?.toLowerCase()) {
                actualPrice = localisePrice(tier.Price);
            }
            if (tier.Discount && tier.Discount.DiscountedMonths) {
                discountedPrice = localisePrice(tier.Discount.Price);
                discountedMonths = tier.Discount.DiscountedMonths;
                modifiedDescription = offerMonthlyDescription;
            }
        });

        let remainingMonths =
            offerDetail?.Instalment?.TermInMonths - discountedMonths;
        modifiedDescription = modifiedDescription
            .replace(/%\{discountedMonths\}/g, discountedMonths?.toString())
            .replace(/%\{remainingMonths\}/g, remainingMonths?.toString())
            .replace(/%\{discountedPrice\}/g, discountedPrice?.toString())
            .replace(/%\{actualPrice\}/g, actualPrice?.toString())
            .replace(/%\{frequency\}/g, frequency.toString());
    }

    const handleTierButtonClick = () => {
        if (offerDetail?.EntitlementSetId && offerDetail?.TierRank) {
            triggerTierSelectEvent({
                rank: offerDetail?.TierRank,
                entitlementSetId: offerDetail?.EntitlementSetId
            });
        }
        triggerElementClick({
            section: elementClickEventTypes.BODY,
            itemId: `entitlement_set_button_text_${entitlementSetId}_cta`,
            resourceString: buttonLabel
        });
        if (action) {
            action(planSelectorShown, billingPlan, preselectBillingPeriod);
        }
    };

    const logoWidth = (cardWidth - 88) / 6;

    const shouldTurnOffDataSyncHeight =
        showPlanUI &&
        (remainingContentTiers || !showSeeMorePlansUi() || isMobile);

    if (
        billingPeriod == 'Week' &&
        (showWeeklyPlanAtBottom || showWeeklyPlanAtTop)
    ) {
        return null;
    }

    const tierCardProps =
        (version !== 'v1' && isMobile) || shouldTurnOffDataSyncHeight
            ? {}
            : {'data-sync-height': 'TierCard'};
    const tierCardBenefitsProps =
        (version !== 'v1' && isMobile) || shouldTurnOffDataSyncHeight
            ? {}
            : {'data-sync-height': 'TierCardBenefits'};

    let modifiedEyebrowText = eyebrowText
        ?.replace(
            /\{annualSavingsWithInstallment\}/g,
            `${annualSavingsWithInstallment}`
        )
        ?.replace(
            /\{annualSavingsWithMonthly\}/g,
            `${annualSavingsWithMonthly}`
        );

    const handleScrollToElement = (elementId: string) => {
        const element = document.getElementById(elementId);
        if (element) {
            const elementRect = element.getBoundingClientRect();
            const scrollOffset =
                window.pageYOffset || document.documentElement.scrollTop;
            // Calculate the top position to scroll to (15px below the viewport top)
            const targetScrollPosition = scrollOffset + elementRect.top - 15; // Adjust to 15px below the top
            // Smoothly scroll to the calculated position
            window.scrollTo({
                top: targetScrollPosition,
                behavior: 'smooth'
            });
        }
    };

    const shouldApplyForVersion3 = version === 'v3' && isMobile;

    return (
        <div
            {...tierCardProps}
            id={`lp-alib-content-tiers-card-item-index-${index}`}
            data-card-highlighted={isCardHighlighted?.toString()}
            className={classnames(styles.cardContainer, {
                [styles.cardHighlighted]: isCardHighlighted && !isEyebrowAdded,
                [styles.cardEyebrowHighlighted]:
                    isCardHighlighted && isEyebrowAdded && !showPlanUI,
                [styles.cardEyebrowAdded]: !isCardHighlighted && isEyebrowAdded,
                [styles.cardContainerNewUI]: showPlanUI,
                [styles.cardContainerNewUISelected]:
                    showPlanUI && isCardHighlighted,
                [styles.cardContainerMobileV2]: version !== 'v1',
                [styles.cardEyebrowHighlightedV2]:
                    isCardHighlighted &&
                    isEyebrowAdded &&
                    !showPlanUI &&
                    version !== 'v1',
                [styles.v3CardEyebrowHighlighted]: version === 'v3'
            })}
        >
            {isEyebrowAdded && !showPlanUI && (
                <Markdown className={classnames(styles.cardEyebrow)}>
                    {modifiedEyebrowText}
                </Markdown>
            )}
            <div
                className={classnames(styles.tierCard, {
                    [styles.tierCardNewUI]: showPlanUI,
                    [styles.cardHighlighted]: isCardHighlighted,
                    [styles.isEyebrowAdded]:
                        isEyebrowAdded && !isCardHighlighted,
                    [styles.isEyebrowAddedSingleCard]:
                        isEyebrowAdded && showPlanUI,
                    [styles.cardContainerV2]: version !== 'v1'
                })}
                id={'card-item-alib-' + position}
                onClick={handleTierButtonClick}
            >
                {isEyebrowAdded && showPlanUI && (
                    <div className={styles.cardEyebrowPlanUiWrapper}>
                        <Markdown className={styles.cardEyebrowPlanUi}>
                            {eyebrowText}
                        </Markdown>
                    </div>
                )}
                {showPlanUI &&
                    (!isEyebrowAdded || !eyebrowText?.length) &&
                    !remainingContentTiers && (
                        <div
                            data-sync-height="TierCardEyebrow"
                            className={classNames(
                                styles.cardEyebrowPlanUiWrapper,
                                {
                                    [styles.cardEyebrowNewPlanUiHeightSync]:
                                        showPlanUI &&
                                        (!isEyebrowAdded ||
                                            !eyebrowText?.length)
                                }
                            )}
                        ></div>
                    )}
                {showImage && image?.src && (
                    <div
                        data-sync-height="TierCardImage"
                        className={classnames(styles.cardImageContainer, {
                            [styles.isEyebrowAdded]:
                                isEyebrowAdded && !isCardHighlighted
                        })}
                    >
                        <img src={image.src} alt="Tier Image" />
                        <div
                            data-sync-height="TierCardMetaData"
                            className={styles.cardMetaDataWithImage}
                        >
                            <CardHeader
                                contentTier={contentTier}
                                offerDetail={offerDetail}
                                bestValueBadgeShown={bestValueBadgeShown}
                                showImage={true}
                                billingPlan={billingPlan}
                                planSelectorShown={planSelectorShown}
                                showPlanUI={showPlanUI}
                                isEyebrowAdded={isEyebrowAdded}
                                comparedOffer={comparedOffer}
                                modifiedDescription={modifiedDescription}
                                remainingContentTiers={remainingContentTiers}
                                version={version}
                                onV3ChevronClick={onV3ChevronClick}
                                v3ClickedChevron={v3ClickedChevron}
                                index={index}
                                handleScrollToElement={handleScrollToElement}
                            />
                        </div>
                    </div>
                )}
                {!(showImage && Boolean(image?.src)) && (
                    <div
                        {...(!isMobile &&
                            !remainingContentTiers && {
                                'data-sync-height': 'TierCardMetaData'
                            })}
                        className={classnames({
                            [styles.NewTierPriceContainer]: showPlanUI,
                            [styles.noMargin]:
                                isEyebrowAdded &&
                                showPlanUI &&
                                isHighlightedMiddleTier,
                            [styles.w100]: shouldApplyForVersion3
                        })}
                    >
                        <CardHeader
                            contentTier={contentTier}
                            offerDetail={offerDetail}
                            bestValueBadgeShown={bestValueBadgeShown}
                            showImage={false}
                            billingPlan={billingPlan}
                            planSelectorShown={planSelectorShown}
                            showPlanUI={showPlanUI}
                            comparedOffer={comparedOffer}
                            modifiedDescription={modifiedDescription}
                            isEyebrowAdded={isEyebrowAdded}
                            remainingContentTiers={remainingContentTiers}
                            version={version}
                            onV3ChevronClick={onV3ChevronClick}
                            v3ClickedChevron={v3ClickedChevron}
                            index={index}
                            handleScrollToElement={handleScrollToElement}
                        />
                    </div>
                )}
                {modifiedDescription && !showPlanUI && (
                    <div
                        data-sync-height="TierCardDescription"
                        className={styles.cardDescription}
                    >
                        <Markdown>{modifiedDescription}</Markdown>
                    </div>
                )}

                <Wrapper
                    condition={v3ClickedChevron === index}
                    version={shouldApplyForVersion3}
                >
                    {interactionType === ContentTierInteractionType.BUTTON &&
                        buttonLabel &&
                        !showPlanUI &&
                        (isQueryParamTrue('seo') ? (
                            <a
                                href={contentTier?.button?.buttonHref}
                                data-sync-height="TierCardButton"
                                className={classnames(styles.cardButton, {
                                    [styles.cardButtonV2]: version !== 'v1'
                                })}
                            >
                                <Markdown>{applyBold(buttonLabel)}</Markdown>
                            </a>
                        ) : (
                            <button
                                data-sync-height="TierCardButton"
                                className={classnames(styles.cardButton, {
                                    [styles.goldenv2]:
                                        buttonType == ButtonTypes.GOLDEN,
                                    [styles.cardButtonV2]: version !== 'v1'
                                })}
                            >
                                {buttonImage && buttonImage?.src && (
                                    <img
                                        className={styles.btnImg}
                                        src={buttonImage?.src}
                                    />
                                )}
                                <Markdown>{applyBold(buttonLabel)}</Markdown>
                            </button>
                        ))}
                    {showDivider && (
                        <p
                            data-sync-height="TierCardDivider"
                            className={classNames(styles.cardContentDivider, {
                                [styles.cardContentDividerNewUI]: showPlanUI
                            })}
                        />
                    )}
                    {tierLogos?.length > 0 && (
                        <div
                            data-sync-height="TierCardLogos"
                            className={styles.competitionLogoContainer}
                        >
                            {tierLogos?.map((url: string, index: number) => (
                                <img
                                    key={index + 1}
                                    alt="logo"
                                    src={url}
                                    width={logoWidth}
                                    height={logoWidth}
                                />
                            ))}
                        </div>
                    )}
                    {showBenefits && benefits?.length > 0 && (
                        <div
                            className={classNames(styles.benefitsContainer, {
                                [styles.benefitsContainerNewUI]: showPlanUI
                            })}
                            {...tierCardBenefitsProps}
                        >
                            {benefits?.map(
                                (
                                    benefit: IContentTierBenefit,
                                    index: number
                                ) => (
                                    <BenefitComponent
                                        key={index + 1}
                                        benefit={benefit}
                                        showPlanUI={showPlanUI}
                                        annualSavingsWithInstallment={
                                            annualSavingsWithInstallment?.toString() ??
                                            ''
                                        }
                                        annualSavingsWithMonthly={
                                            annualSavingsWithMonthly?.toString() ??
                                            ''
                                        }
                                        version={version}
                                        priceToShow={priceToShow}
                                    />
                                )
                            )}
                        </div>
                    )}
                    {(!showBenefits || !benefits?.length) && (
                        <div
                            data-sync-height="TierCardBenefits"
                            className={styles.benefitsContainer}
                        ></div>
                    )}
                    {interactionType === ContentTierInteractionType.BUTTON &&
                        buttonLabel &&
                        showPlanUI &&
                        (isQueryParamTrue('seo') ? (
                            <a
                                href={contentTier?.button?.buttonHref}
                                data-sync-height="TierCardButton"
                                className={classnames(styles.cardButton, {
                                    [styles.cardButtonV2]: version !== 'v1'
                                })}
                            >
                                <Markdown>{applyBold(buttonLabel)}</Markdown>
                            </a>
                        ) : (
                            <button
                                data-sync-height="TierCardButton"
                                className={classNames(styles.cardButton, {
                                    [styles.golden]: buttonType == 'golden',
                                    [styles.cardButtonNewUI]: showPlanUI,
                                    [styles.cardButtonV2]: version !== 'v1'
                                })}
                            >
                                <Markdown>{applyBold(buttonLabel)}</Markdown>
                            </button>
                        ))}
                </Wrapper>
            </div>
        </div>
    );
};

export {ContentTier};
